/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */
import React from "react";
import divorcePlannerStyle from "./divorcePlannerStyle";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import childImage from "../../assets/img/planner/icon_child_150px.png";
import Typography from "@mui/material/Typography";
import { Box, Link } from "@mui/material";
import LinkLegalbird from "@/src/components/legalbird/LinkLegalbird/LinkLegalbird";
import { mergeSx } from "@/src/utils/mergeSx";

type DivorcePlannerChildrenProps = {
  id: string;
};

const DivorcePlannerChildren = ({ id }: DivorcePlannerChildrenProps) => {
  return (
    <Paper sx={divorcePlannerStyle.contentContainer} elevation={4}>
      <Box id={id} sx={divorcePlannerStyle.scrollToAnchor} />
      <Grid container sx={mergeSx({ justifyContent: "center" }, divorcePlannerStyle.headlineContainer)}>
        <Grid size={12}>
          <Grid
            container
            sx={{
              alignItems: "center",
            }}
          >
            <Grid size={5}>
              <Box sx={divorcePlannerStyle.imageContainer}>
                <Paper sx={divorcePlannerStyle.imageCircle}>
                  <img src={childImage} alt={"Trennung mit Kindern"} />
                </Paper>
              </Box>
            </Grid>
            <Grid size={7}>
              <Typography variant={"cardTitle"}>Trennung mit Kindern</Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <p>
        Wenn Sie sich scheiden lassen und gemeinsame Kinder haben, gibt es neben rechtlichen auch viele emotionale Themen, mit denen Ihre Familie umgehen muss.
        Wir haben Ihnen einige Tipps zum Umgang mit der neuen Situation zusammengestellt und kurz aufgeführt, was Sie aus rechtlicher Sicht bedenken müssen.
      </p>
      <Typography variant={"h6"}>
        <strong>Welche rechtlichen Themen müssen wir klären?</strong>
      </Typography>
      <p>
        Aus rechtlicher Sicht geht es bei einer Trennung vor allem darum, wie die Erziehung zukünftig organisiert und der Unterhalt der Kinder finanziert werden
        soll. Kurz zusammengefasst müssen Sie gemeinsam mit Ihrem Partner folgende Themen klären:
      </p>
      <ul>
        <li>
          <strong>Sorgerecht:</strong> Als verheiratete Eltern besitzen Sie automatisch das gemeinsame Sorgerecht für Ihr Kind. Dies bleibt auch nach einer
          Scheidung so bestehen, es sei denn, einer der Ehepartner beantragt den alleinigen Anspruch auf Sorgerecht beim Familiengericht.
        </li>
        <li>
          <strong>Umgangsrecht:</strong> Kinder haben ein Umgangsrecht für beide Eltern, dessen Gestaltung Sie individuell regeln können. Wenn Sie sich hier
          nicht einigen können, entscheidet das Gericht darüber.
        </li>
        <li>
          <strong>Kindesunterhalt:</strong> Die Sicherung der Versorgungssituation der Kinder hat hohe Priorität. Kinder haben immer ein Recht auf Unterhalt.
          Die Höhe des Unterhalts richtet sich nach Kindesalter und Einkommen der Ehepartner und orientiert sich an den Richtgrößen der sogenannten
          „Düsseldorfer Tabelle“.
        </li>
      </ul>
      <Typography variant={"h6"}>
        <strong>Sorgerecht</strong>
      </Typography>
      <p>
        Eine Scheidung ändert nichts an dem gemeinsamen Sorgerecht der Eltern. Auch als geschiedene Eltern haben Sie also weiterhin die gemeinsame elterliche
        Sorge - außer, Sie möchten eine andere Regelung finden.
      </p>
      <p>Insgesamt sind drei Bereiche der gemeinsamen elterlichen Sorge bei einer Trennung zu unterscheiden:</p>
      <ul>
        <li>
          <strong>Entscheidungen in Angelegenheiten des täglichen Lebens:</strong> Die Entscheidungen in Angelegenheiten des täglichen Lebens trifft der
          Elternteil, bei dem das Kind sich gewöhnlich aufhält, sprich wo es wohnt. Dazu gehören z.B. Entscheidungen über schulische Nachhilfe, die Behandlung
          leichter Erkrankungen oder der Aufenthalt bei Verwandten.
        </li>
        <li>
          <strong>Entscheidungen in Angelegenheiten der tatsächlichen Betreuung: </strong> In Angelegenheiten der tatsächlichen Betreuung entscheidet immer der
          Elternteil, bei dem sich das Kind in der Situation gerade aufhält. Beispiele dafür sind z.B. die Art der Ernährung oder die Schlafenszeiten.
        </li>
        <li>
          <strong>Entscheidungen in Angelegenheiten von erheblicher Bedeutung: </strong> Entscheidungen in Angelegenheiten von erheblicher Bedeutung können die
          Eltern bei gemeinsamem Sorgerecht auch immer nur gemeinsam treffen. Gegenstand solcher Entscheidungen ist z.B. die Wahl der Schule, die Zustimmung zu
          Operationen (außer in Eilfällen), das Vermögen oder Grundentscheidungen über den Lebensmittelpunkt des Kindes, wie zum Beispiel den Wohnort.
        </li>
      </ul>
      <Typography variant={"h6"}>
        <strong>Umgangsrecht</strong>
      </Typography>
      <p>
        Für eine ungestörte, ausgeglichene Entwicklung braucht Ihr Kind bestenfalls beide Elternteile an seiner Seite. Der Elternteil, bei dem das Kind nicht
        lebt, hat daher das Recht sein Kind regelmäßig zu sehen. Das Umgangsrecht ist nicht davon abhängig, ob Unterhalt gezahlt wird oder nicht. Wenn Sie als
        Paar auseinander gehen, sollten Sie daher trotzdem immer versuchen, gemeinsam für Ihre Kinder da zu sein. Dazu müssen Sie als Eltern miteinander in
        Kontakt und im Gespräch bleiben. Es ist wichtig, dass Sie Ihrem Kind auch nach der Trennung die Sicherheit vermitteln, dass es von Ihnen beiden
        uneingeschränkt geliebt und versorgt wird. Dazu gehört für das Kind vor allem auch der regelmäßige Kontakt mit dem Elternteil, der nun nicht mehr im
        selben Haushalt lebt.
      </p>
      <p>
        Eine allgemein gültige und optimale Umgangsregelung gibt es nicht, denn sie muss den Bedürfnissen und Möglichkeiten sowohl des Kindes als auch seiner
        Eltern angepasst werden. Niemand kann diese Bedürfnisse besser einschätzen, als Sie und Ihr Ex-Partner.
      </p>
      <p>
        Sollte es zwischen Ihnen und Ihrem Ex-Partner viele Streitpunkte geben, ist es umso wichtiger, eine Umgangsregelung inhaltlich konkret auszuarbeiten.
        Nur so kann vermieden werden, dass es in der Zukunft zu Unstimmigkeiten und Missverständnissen kommt. Vereinbaren Sie daher zeitnah als Eltern gemeinsam
        ein "Grundgerüst" für den Umgang. Nach Absprache kann die Umgangsregelung jederzeit wieder geändert und an die Gegebenheiten angepasst werden.
      </p>
      <p>
        Wenn Sie sich nicht auf eine Regelung einigen können, gibt es die Möglichkeit, beim örtlichen Jugendamt kostenlose Elterngespräche zu führen, um
        gemeinsam eine außergerichtliche Lösung zu finden. Der Schritt vor Gericht sollte immer nur im Notfall gegangen werden.
      </p>
      <Typography variant={"h6"}>
        <strong>Kindesunterhalt</strong>
      </Typography>
      <p>
        Die Unterhaltsverpflichtung gegenüber Ihrem Kind besteht für beide Elternteile, wobei zwischen dem Barunterhalt und dem Betreuungsunterhalt zu
        differenzieren ist.
      </p>
      <p>
        Der Elternteil, bei dem Ihr Kind lebt und der somit für die Betreuung und Pflege zuständig ist, leistet den Betreuungsunterhalt. Der andere Elternteil
        hingegen muss finanzielle Leistungen erbringen; den sogenannten Barunterhalt. Die Höhe des Barunterhalts wird aus den individuellen Einkommens- und
        Vermögensverhältnissen berechnet. Mehr Informationen zum Thema Kindesunterhalt finden Sie im{" "}
        <LinkLegalbird to={"/scheidung/unterhalt"}>Ratgebertext zum Unterhalt</LinkLegalbird>.
      </p>
      <p>Bei tiefergehenden, rechtlichen Fragen zum Thema Sorgerecht, Umgangsrecht oder Kindesunterhalt helfen wir Ihnen sehr gerne weiter!</p>
      <Typography variant={"h6"}>
        <strong>Tipps für den Umgang mit der neuen Situation</strong>
      </Typography>
      <p>
        Neben den rechtlichen Themen bringt eine Scheidung besonders für Ihr Kind eine große Unsicherheit mit sich. Zielsetzung sollte daher immer sein, alle
        negative Auswirkungen für Ihr Kind zu minimieren. Im Folgenden finden Sie daher einige Tipps für eine friedliche und faire Scheidung, die allen
        Beteiligten die Situation erleichtert:
      </p>
      <ul>
        <li>Die Stimmung im Auge behalten: offene und faire Kommunikation mit dem Partner.</li>
        <li>Informieren: frühzeitig informieren und durch einfache Erklärungen Sicherheit geben.</li>
        <li>Mit einbeziehen: das Kind mitentscheiden lassen, indem Sie z.B. Varianten für die neuen Regelungen zur Auswahl stellen.</li>
      </ul>
      <Typography variant={"h6"}>Kommunizieren Sie fair und offen</Typography>
      <p>
        Wie Sie als Ex-Partner miteinander und mit der Scheidung umgehen, wirkt sich direkt auf Ihr Kind aus. Daher gilt: versuchen Sie trotz der vielen
        Emotionen offen und fair mit Ihrem Partner zu kommunizieren. Kinder spüren Stimmungen und übertragen sie oft auf sich und geben sich die Schuld für die
        Trennung ihrer Eltern. Die Veränderung und insbesondere die Ungewissheit sind sehr verunsichernd für Ihr Kind. Durch eine spürbar negative Stimmung
        zwischen den Eltern wird diese Versunsicherung unnötig verstärkt.
      </p>
      <Typography variant={"h6"}>Durch einfache Erklärungen Sicherheit geben</Typography>
      <p>
        Ohne Erklärung ist die neue Situation für Kinder oft nicht nachvollziehbar und kann sehr beängstigend sein. Daher ist es sehr wichtig, dass Sie Ihrem
        Kind die neue Situation möglichst früh ganz offen und einfach erklären. Vermeiden Sie dabei, Ihren Ex-Partner schlecht zu machen oder ihm Schuld
        zuzuweisen. Hierbei hilft: Erklären Sie die Situation nicht aus Ihrer Sicht, sondern aus Sicht des Kindes: Was ändert sich im Leben Ihres Kindes durch
        die Scheidung? Geben Sie Ihrem Kind Sicherheit, indem Sie die neuen Abläufe und Regelungen genau schildern und diese ggf. sogar aufschreiben/aufmalen.
      </p>
      <Typography variant={"h6"}>Kinder in die neue Lebensplanung einbeziehen</Typography>
      <p>
        Während Sie die neue Situation besprechen, sollten Sie Ihr Kind nicht vor vollendete Tatsachen stellen, sondern nachhören, wie es sich die neue
        Situation wünschen würde. Stellen Sie bestenfalls verschiedene Varianten zur Verfügung, aus denen das Kind auswählen kann - zum Beispiel bei der
        Aufteilung der Besuchszeiten können verschiedene Varianten vorgeschlagen werden. Zudem kann es helfen, die neuen Regelungen nicht als ganz final zu
        beschreiben, sondern zu erklären, dass sie zunächst ausprobiert werden, um dann die Lösung zu finden, die für alle am besten passt.
      </p>
      <Typography variant={"h6"}>
        <strong>Beratungsstellen</strong>
      </Typography>
      <p>Bei allen Fragen rund um das Thema Kinder und Scheidung helfen Ihnen auch die folgenden Stellen weiter:</p>
      <ul>
        <li>
          <Link href={"http://www.unterstuetzung-die-ankommt.de/de/"} target={"_blank"} rel="noopener noreferrer">
            Ihr örtlich zuständiges Jugendamt
          </Link>
        </li>
        <li>
          <Link href={"http://www.caritas.de/hilfeundberatung/onlineberatung/elternundfamilie/elternundfamilie"} target={"_blank"} rel="noopener noreferrer">
            Caritas
          </Link>
        </li>
        <li>
          <Link href={"http://www.profamilia.de/themen/sexualitaet-und-partnerschaft/trennung-und-scheidung.html"} target={"_blank"} rel="noopener noreferrer">
            ProFamilia
          </Link>
        </li>
        <li>„freie“ Beratungsstellen, wie etwa kirchliche Einrichtungen</li>
      </ul>
    </Paper>
  );
};

export default DivorcePlannerChildren;
